import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import OpenSeadragon from "openseadragon";
import tmdbApi from "../api/tmdbApi";
import moment from "moment";

const SharedDziView = () => {
    const { shareId } = useParams();
    const viewerRef = useRef(null);
    const osdViewerRef = useRef(null);
    // const expiryTimerRef = useRef(null);
    const [status, setStatus] = useState("loading"); // "loading" | "valid" | "expired" | "invalid"
    const [shareData, setShareData] = useState(null);
    const [cloudfrontUrl, setCloudfrontUrl] = useState("");
    const [imageLoading, setImageLoading] = useState(true);

    useEffect(() => {
        tmdbApi.configData().then(res => {
            const raw = res?.result?.[0]?.common?.imageCloudfront || "";
            const cf = raw.replace(/^http:\/\//i, 'https://');
            if (cf) setCloudfrontUrl(cf);
        }).catch(() => {});

        if (shareId) {
            console.log("[SharedDziView] Share ID from URL:", shareId);
            validateShare();
        } else {
            console.warn("[SharedDziView] No shareId in URL — marking invalid.");
            setStatus("invalid");
        }

        // return () => {
        //     if (expiryTimerRef.current) {
        //         clearTimeout(expiryTimerRef.current);
        //         expiryTimerRef.current = null;
        //     }
        // };
    }, [shareId]);

    // client-side expiry timer disabled - expiry is decided only by the api
    // const scheduleExpiryTimer = (created, hours) => {
    //     if (expiryTimerRef.current) {
    //         clearTimeout(expiryTimerRef.current);
    //         expiryTimerRef.current = null;
    //     }
    //     const expiryTime = moment(created).add(hours, "hours");
    //     // setTimeout overflows (fires immediately) for delays > 2^31-1 ms (~24.8 days),
    //     // so long waits are scheduled in chunks and re-checked on each tick.
    //     const MAX_TIMEOUT_MS = 2147483647;
    //     const tick = () => {
    //         const msUntilExpiry = expiryTime.diff(moment());
    //         if (msUntilExpiry <= 0) {
    //             console.log("[SharedDziView] Client-side expiry timer fired — marking expired.");
    //             setStatus("expired");
    //             return;
    //         }
    //         expiryTimerRef.current = setTimeout(tick, Math.min(msUntilExpiry, MAX_TIMEOUT_MS));
    //     };
    //     if (expiryTime.diff(moment()) > 0) {
    //         console.log("[SharedDziView] Scheduling client-side expiry timer in", expiryTime.diff(moment()), "ms");
    //         tick();
    //     }
    // };

    const validateShare = async () => {
        console.log("[SharedDziView] Validating share link for ID:", shareId);
        try {
            const response = await tmdbApi.validateShareUrl(shareId);
            console.log("[SharedDziView] validateShareUrl response:", response?.result);

            // API-based expiry fallback check
            if (response?.result === "This link has expired" || response?.result === "No active share case found for this URL") {
                console.warn("[SharedDziView] Share link expired (API response).");
                setStatus("expired");
                return;
            }

            if (response?.result) {
                const data = response.result?.[0] || response.result;
                console.log("[SharedDziView] Share link valid — data:", data);

                // expiry is decided only by the api - client-side created + hours check disabled
                // if (data?.created && data?.hours != null) {
                //     const expiryTime = moment(data.created).add(data.hours, "hours");
                //     if (moment().isAfter(expiryTime)) {
                //         console.warn("[SharedDziView] Share link expired (client-side check).");
                //         setStatus("expired");
                //         return;
                //     }
                //     // Schedule real-time expiry while the user is on the page
                //     scheduleExpiryTimer(data.created, data.hours);
                // }

                setShareData(data);
                setStatus("valid");
            } else {
                console.warn("[SharedDziView] Unexpected response shape — marking invalid:", response?.result);
                setStatus("invalid");
            }
        } catch (err) {
            const httpStatus = err?.response?.status;
            console.error("[SharedDziView] validateShareUrl error — HTTP status:", httpStatus, err?.message || err, err?.response?.data);
            if (httpStatus === 410) {
                setStatus("expired");
            } else if (httpStatus === 404) {
                setStatus("invalid");
            } else {
                setStatus("invalid");
            }
        }
    };

    useEffect(() => {
        if (status !== "valid" || !shareData || !cloudfrontUrl) return;
        // viewerRef.current may not be set yet on this render cycle — use a short defer
        const timer = setTimeout(() => {
            if (viewerRef.current) {
                initViewer();
            }
        }, 0);
        return () => {
            clearTimeout(timer);
            if (osdViewerRef.current) {
                osdViewerRef.current.destroy();
                osdViewerRef.current = null;
            }
        };
    }, [status, shareData, cloudfrontUrl]);

    const initViewer = () => {
        const rawCf = shareData?.imageCloudfront || cloudfrontUrl || "";
        const imageCloudfront = rawCf.replace(/^http:\/\//i, 'https://');

        if (!imageCloudfront) return;

        if (osdViewerRef.current) {
            osdViewerRef.current.destroy();
            osdViewerRef.current = null;
        }

        // Exact same logic as editLearningcase JPEG/TIFF click handler
        let path = shareData?.dziUrl || "";
        if (path.startsWith("/")) path = path.slice(1);
        const isDziURL = path.includes("output.dzi");
        if (isDziURL) path = path + "_files/";

        const tileSource = isDziURL
            ? {
                Image: {
                    xmlns: "http://schemas.microsoft.com/deepzoom/2008",
                    Url: encodeURI(imageCloudfront + path),
                    Format: "jpeg",
                    Overlap: 2,
                    TileSize: 256,
                    Size: { Width: shareData?.width, Height: shareData?.height },
                },
            }
            : {
                type: "image",
                url: encodeURI(imageCloudfront + (shareData?.sourcepath || "")),
                buildPyramid: false,
            };

        const viewer = OpenSeadragon({
            id: "shared-dzi-viewer",
            prefixUrl: "https://openseadragon.github.io/openseadragon/images/",
            tileSources: tileSource,
            showNavigator: true,
            crossOriginPolicy: "Anonymous",
            navigatorPosition: "BOTTOM_RIGHT",
            maxZoomPixelRatio: 10,
            minZoomImageRatio: 0.5,
            visibilityRatio: 1,
            zoomPerScroll: 2,
            homeFillsViewer: true,
            gestureSettingsMouse: {
                clickToZoom: true,
                dblClickToZoom: true,
                flickEnabled: true,
                pinchToZoom: true,
                scrollToZoom: true,
            },
            showZoomControl: true,
            showHomeControl: true,
            showFullPageControl: true,
            showRotationControl: true,
            wrapHorizontal: false,
        });

        setImageLoading(true);
        viewer.addHandler("open", () => setImageLoading(false));
        viewer.addHandler("open-failed", () => setImageLoading(false));
        osdViewerRef.current = viewer;
    };


    const renderHeader = () => (
        <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 20px",
            backgroundColor: "#1E222B",
            borderBottom: "1px solid #333",
        }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span className="material-symbols-outlined" style={{ color: "#F2A341", fontSize: "28px" }}>
                    biotech
                </span>
                <div>
                    <div style={{ color: "#fff", fontWeight: 700, fontSize: "16px", lineHeight: 1.2 }}>
                        Histology Image Viewer
                    </div>
                    {shareData?.filename && (
                        <div style={{ color: "#aaa", fontSize: "12px" }}>{shareData.filename}</div>
                    )}
                </div>
            </div>
            {shareData?.expiresAt && (
                <div style={{
                    color: "#aaa",
                    fontSize: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                }}>
                    <span className="material-symbols-outlined" style={{ fontSize: "16px", color: "#F2A341" }}>
                        schedule
                    </span>
                    Expires: {moment(shareData.expiresAt).format("DD MMM YYYY, hh:mm A")}
                </div>
            )}
        </div>
    );

    if (status === "loading") {
        return (
            <div style={{
                minHeight: "100vh",
                backgroundColor: "#1a1a2e",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "20px",
            }}>
                <div className="spinner-border text-warning" role="status" style={{ width: "48px", height: "48px" }}>
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p style={{ color: "#ccc", fontSize: "16px", margin: 0 }}>Validating share link...</p>
            </div>
        );
    }

    if (status === "expired") {
        return (
            <div style={{
                minHeight: "100vh",
                backgroundColor: "#1a1a2e",
                display: "flex",
                flexDirection: "column",
            }}>
                {renderHeader()}
                <div style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "16px",
                    padding: "40px",
                }}>
                    <div style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        backgroundColor: "rgba(220, 83, 84, 0.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "8px",
                    }}>
                        <span className="material-symbols-outlined" style={{ fontSize: "42px", color: "#DC5354" }}>
                            timer_off
                        </span>
                    </div>
                    <h2 style={{ color: "#fff", fontWeight: 700, margin: 0 }}>Link Expired</h2>
                    <p style={{ color: "#aaa", textAlign: "center", maxWidth: "400px", margin: 0 }}>
                        This share link has expired. 
                    </p>
                </div>
            </div>
        );
    }

    if (status === "invalid") {
        return (
            <div style={{
                minHeight: "100vh",
                backgroundColor: "#1a1a2e",
                display: "flex",
                flexDirection: "column",
            }}>
                {renderHeader()}
                <div style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "16px",
                    padding: "40px",
                }}>
                    <div style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        backgroundColor: "rgba(220, 83, 84, 0.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "8px",
                    }}>
                        <span className="material-symbols-outlined" style={{ fontSize: "42px", color: "#DC5354" }}>
                            link_off
                        </span>
                    </div>
                    <h2 style={{ color: "#fff", fontWeight: 700, margin: 0 }}>Invalid Link</h2>
                    <p style={{ color: "#aaa", textAlign: "center", maxWidth: "400px", margin: 0 }}>
                        This share link is invalid or does not exist. Please check the URL and try again.
                    </p>
                </div>
            </div>
        );
    }

    // Valid — show DZI viewer
    return (
        <div style={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#000",
        }}>
            {/* Header */}
            {renderHeader()}


            {/* Viewer */}
            <div style={{ flex: 1, position: "relative" }}>
                {imageLoading && (
                    <div style={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: "#000",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "16px",
                        zIndex: 10,
                    }}>
                        <div className="spinner-border text-warning" role="status" style={{ width: "48px", height: "48px" }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p style={{ color: "#ccc", fontSize: "15px", margin: 0 }}>Loading image...</p>
                    </div>
                )}
                <div
                    id="shared-dzi-viewer"
                    ref={viewerRef}
                    style={{ width: "100%", height: "100%" }}
                />
            </div>
              <div
                    style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: "grey",
                        fontSize: "2vw",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "2px",
                        textShadow: "1px 1px 4px rgba(0,0,0,0.6)",
                        pointerEvents: "none",
                        zIndex: 10,
                        userSelect: "none"
                    }}
                >
                    Copyright © 2026 DADI
                </div>
        </div>
    );
};

export default SharedDziView;
